import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  AlertTriangle, ArrowRight, Check, CreditCard, Info, Loader2, MapPin, Minus,
  Monitor, PackageCheck, PackageOpen, Plus, ShieldCheck, Trash2, Truck,
} from 'lucide-react';
import {
  BUSINESS, formatPrice, getCartAddonSuggestions, getCartWarrantyOptions, getImageUrl,
  getProduct, HERO_IMAGES,
} from '../config';
import { maxQtyFor, useCart } from '../context/CartContext';
import SpecialOrderModal from '../components/SpecialOrderModal';

// Seule disponibilité servie depuis l'atelier ; miroir exact de
// IN_STOCK_AVAILABILITY côté backend (services/deposit_svc.py), qui décide du
// mode de règlement. Le serveur reste l'autorité : ici on ne fait qu'annoncer.
const IN_STOCK = 'stock_meteris';
const UNIQUE_CONDITION = 'reconditionne';

// Formulations positives, cohérentes avec la fiche produit : un article
// commandable n'est jamais présenté comme un problème d'approvisionnement.
function availabilityInfo(item) {
  if (item.availability === IN_STOCK) {
    return { Icon: PackageCheck, label: 'En stock — retrait immédiat' };
  }
  return { Icon: Truck, label: 'Disponible sous quelques jours' };
}

// L'API publique renomme `product_id` en `id` et n'expose les visuels que via
// `images[]` : traduction vers les champs de l'instantané du panier.
function snapshotFromProduct(product) {
  return {
    name: product?.name || '',
    price: Number(product?.price) || 0,
    image: product?.images?.[0] ?? product?.image ?? product?.image_url ?? null,
    availability: product?.availability || 'stock_meteris',
    condition: product?.condition || UNIQUE_CONDITION,
    stock: product?.stock ?? null,
  };
}

// Traduction d'un produit servi par l'API publique vers ce qu'attend `addItem`.
//
// ⚠️ `addItem` sort SANS RIEN FAIRE et sans erreur quand l'objet reçu ne porte
// pas de `product_id` — or l'API publique renomme ce champ en `id` et n'expose
// les visuels que via `images[]`. Passer une suggestion telle quelle rendrait le
// bouton d'ajout silencieusement mort (piège déjà rencontré au V1). Les autres
// champs (prix, état, stock, disponibilité) portent déjà le bon nom.
function addableFromApiProduct(product) {
  return {
    ...product,
    product_id: product?.id ?? product?.product_id,
    image: product?.images?.[0] ?? product?.image ?? null,
  };
}

// Écarts entre l'instantané figé à l'ajout et la fiche relue à l'ouverture du
// panier. Le client doit les voir ICI : il ne doit pas découvrir au paiement
// qu'il paie un autre prix que celui affiché.
function driftMessages(before, fresh) {
  if (!fresh || fresh.status !== 'available' || fresh.archived) {
    return { blocking: true, messages: ['Cet article n’est plus disponible à la vente.'] };
  }
  // Stock retombé à zéro alors que la fiche reste publiée : le serveur refusera
  // la ligne (« 0 unité(s) disponible(s) »). Bloquant, donc — sinon la quantité
  // serait ramenée à 0 et le client irait au paiement avec un panier condamné.
  if (maxQtyFor(snapshotFromProduct(fresh)) === 0) {
    return { blocking: true, messages: ['Cet article est en rupture : il n’est plus commandable.'] };
  }
  const messages = [];
  const priceBefore = Math.round((Number(before.price) || 0) * 100);
  const priceAfter = Math.round((Number(fresh.price) || 0) * 100);
  if (priceBefore !== priceAfter) {
    messages.push(
      `Le prix a changé depuis votre ajout : ${formatPrice(before.price)} → ${formatPrice(fresh.price)}.`,
    );
  }
  const wasInStock = before.availability === IN_STOCK;
  const isInStock = (fresh.availability || 'stock_meteris') === IN_STOCK;
  if (wasInStock !== isInStock) {
    messages.push(isInStock
      ? 'Cet article est désormais en stock, retrait immédiat.'
      : 'Cet article n’est plus en stock immédiat : il sera commandé chez notre fournisseur.');
  }
  // Le rafraîchissement ré-écrête la quantité sur le stock relu : ce serait la
  // dernière modification silencieuse du panier si on ne l'annonçait pas.
  const max = maxQtyFor(snapshotFromProduct(fresh));
  if ((Number(before.qty) || 0) > max) {
    messages.push(`La quantité disponible a baissé : votre ligne passe à ${max}.`);
  }
  return { blocking: false, messages };
}

// Un refus serveur qui porte sur la GARANTIE et non sur le produit couvert.
//
// Le serveur rattache ces erreurs au `product_id` du produit COUVERT — il n'existe
// pas d'identifiant de ligne côté client —, si bien qu'elles s'affichent sur la
// ligne du PC. Sans cette distinction, les seuls remèdes offerts sont « Ramener
// à N » (sans objet : aucune quantité en cause) et « Retirer du panier », qui
// emporte la machine. Le geste correct est de retirer la garantie seule ; il vit
// normalement dans le bloc d'options, qui n'est justement pas rendu dans ce cas —
// si le serveur refuse la garantie, c'est qu'il n'en propose plus aucune.
//
// Codes concernés : `warranty_orphan`, `warranty_not_eligible`,
// `warranty_unavailable` (cf `services/cart_svc.py::_build_warranty_lines`).
function isWarrantyError(error) {
  return String(error?.code || '').startsWith('warranty_');
}

/**
 * Page panier client (`/panier`, sans paramètre).
 *
 * À ne pas confondre avec `CartShared` (`/panier/:cartUuid`), le panier
 * commercial préparé par le portail METERIS : les deux routes coexistent et un
 * test de routage verrouille cette coexistence.
 *
 * La validation ouvre le tunnel de commande (`SpecialOrderModal`), à qui cette
 * page passe `cartItems`. Contrat attendu de la modale :
 *   - `cartItems`      : les lignes du panier ;
 *   - `cartWarranties` : les Garanties Atelier choisies. Elles voyagent à
 *                        part des lignes produit parce qu'elles n'en sont pas :
 *                        elles ne portent aucun montant à destination du serveur,
 *                        seulement le produit couvert et la durée ;
 *   - `onClose`        : fermeture ;
 *   - `onSuccess`      : commande DÉFINITIVEMENT acquise — réservation confirmée,
 *                        ou capture de paiement confirmée, jamais avant. C'est le
 *                        signal qui vide le panier, et c'est la modale qui en
 *                        connaît l'instant ;
 *   - `onCartInvalid`  : `errors[]` d'un refus 409 `cart_invalid`, remontés ici
 *                        pour être affichés au niveau des lignes fautives —
 *                        c'est sur cette page que le client corrige son panier.
 */
export default function Cart({ maintenance }) {
  const {
    items, warranties, count, total, warrantyTotal,
    addItem, updateQty, removeItem, syncItems, setWarranty, syncWarranties, clear,
  } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [lineErrors, setLineErrors] = useState([]);

  // Instantané tel qu'il a été relu du localStorage, capturé une seule fois :
  // il sert de point de comparaison « avant » pour signaler les écarts.
  const [storedLines] = useState(() => items);
  const [drift, setDrift] = useState({});
  // 'loading' | 'checked' | 'partial' — 'partial' = au moins une ligne n'a pas
  // pu être vérifiée (panne réseau, 500) : on le dit au lieu de faire semblant.
  const [freshness, setFreshness] = useState(storedLines.length > 0 ? 'loading' : 'checked');

  // Relecture des fiches à l'ouverture du panier. Le localStorage n'expire pas :
  // sans ça, un panier vieux de trois mois afficherait un prix périmé et le
  // serveur facturerait le prix du jour — `validate_cart` ne produit AUCUNE
  // erreur sur un écart de prix, l'écart passerait donc en silence.
  // Il n'existe pas d'endpoint de relecture en lot : un panier compte quelques
  // lignes, N appels parallèles sont le compromis raisonnable et chaque échec
  // reste local à sa ligne.
  useEffect(() => {
    if (storedLines.length === 0) return undefined;
    let active = true;

    Promise.allSettled(storedLines.map((line) => getProduct(line.product_id)))
      .then((results) => {
        if (!active) return;
        const snapshots = {};
        const nextDrift = {};
        let unverified = false;

        results.forEach((result, index) => {
          const line = storedLines[index];
          if (result.status === 'fulfilled') {
            const fresh = result.value;
            const verdict = driftMessages(line, fresh);
            if (!verdict.blocking) snapshots[line.product_id] = snapshotFromProduct(fresh);
            if (verdict.messages.length > 0) nextDrift[line.product_id] = verdict;
            return;
          }
          // 404 : le produit a quitté le catalogue, c'est une réponse, pas une panne.
          if (result.reason?.status === 404) {
            nextDrift[line.product_id] = {
              blocking: true,
              messages: ['Cet article n’est plus au catalogue.'],
            };
            return;
          }
          unverified = true;
        });

        syncItems(snapshots);
        setDrift(nextDrift);
        setFreshness(unverified ? 'partial' : 'checked');
      })
      .catch(() => { if (active) setFreshness('partial'); });

    return () => { active = false; };
  }, [storedLines, syncItems]);

  // Accessoires suggérés pour l'ensemble du panier. Chargés à part, APRÈS
  // l'affichage : c'est une vente additionnelle, elle ne doit jamais retarder ni
  // conditionner le panier lui-même. Un échec ne pose donc aucun état — pas de
  // message, pas de bandeau : le client ne perd rien qu'il ait demandé.
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (storedLines.length === 0) return undefined;
    let active = true;

    getCartAddonSuggestions(storedLines.map((line) => line.product_id))
      .then((list) => {
        if (active && Array.isArray(list)) setSuggestions(list);
      })
      .catch(() => { /* option, pas le cœur du parcours : silence assumé */ });

    return () => { active = false; };
  }, [storedLines]);

  // Options de Garantie Atelier, servies par le serveur pour les lignes du
  // panier : `{product_id: [{years, price, service_label}]}`. Le montant vient du
  // barème des réglages, et le libellé du service est un texte décidé côté
  // serveur — NI l'un NI l'autre n'est reconstruit ici, sous peine de diverger de
  // ce que la commande facturera.
  //
  // Comme les suggestions : chargées à part, sans bloquer l'affichage, et
  // muettes en cas d'échec. Une option de vente qui ne se charge pas ne doit pas
  // inquiéter un client qui n'a rien demandé.
  const [warrantyOptions, setWarrantyOptions] = useState({});

  useEffect(() => {
    if (storedLines.length === 0) return undefined;
    let active = true;

    getCartWarrantyOptions(storedLines.map((line) => line.product_id))
      .then((options) => {
        if (!active || !options || Array.isArray(options) || typeof options !== 'object') return;
        setWarrantyOptions(options);
        // Réconciliation d'un choix vieux de plusieurs jours : le montant est
        // repris du serveur, et une garantie qui n'est plus proposée tombe.
        // Sur un appel ÉCHOUÉ, rien n'est appelé : un réseau coupé n'efface pas
        // un choix du client.
        syncWarranties(options);
      })
      .catch(() => { /* option, pas le cœur du parcours : silence assumé */ });

    return () => { active = false; };
  }, [storedLines, syncWarranties]);

  const warrantyByProduct = useMemo(() => {
    const map = new Map();
    warranties.forEach((warranty) => map.set(warranty.target_product_id, warranty));
    return map;
  }, [warranties]);

  // Une suggestion entrée au panier n'est plus une suggestion. Le filtre est
  // local : le serveur exclut déjà ce qui était au panier au moment de l'appel,
  // mais il n'est pas rappelé à chaque ajout.
  const openSuggestions = suggestions.filter((suggestion) => {
    const suggestionId = suggestion?.id ?? suggestion?.product_id;
    return suggestionId && !items.some((item) => item.product_id === suggestionId);
  });

  const errorByProduct = useMemo(() => {
    const map = new Map();
    lineErrors.forEach((error) => {
      if (error?.product_id) map.set(error.product_id, error);
    });
    return map;
  }, [lineErrors]);

  // Une erreur qui ne se rattache à aucune ligne (panier vide côté serveur,
  // produit déjà retiré entre-temps) ne doit pas disparaître en silence.
  const orphanErrors = lineErrors.filter(
    (error) => !error?.product_id || !items.some((item) => item.product_id === error.product_id),
  );

  const forgetError = (productId) => setLineErrors(
    (current) => current.filter((error) => error?.product_id !== productId),
  );

  // Toute modification d'une ligne périme le verdict du serveur sur cette ligne.
  // L'écart de prix ou de disponibilité, lui, RESTE affiché : changer la
  // quantité ne fait pas disparaître le fait que le prix a bougé.
  const changeQty = (productId, qty) => { updateQty(productId, qty); forgetError(productId); };
  const remove = (productId) => {
    removeItem(productId);
    forgetError(productId);
    setDrift((current) => {
      const next = { ...current };
      delete next[productId];
      return next;
    });
  };

  const handleCartInvalid = (errors) => {
    setLineErrors(Array.isArray(errors) ? errors : []);
    // Le tunnel se ferme : les erreurs se lisent et se corrigent sur les lignes.
    setCheckoutOpen(false);
  };

  // Le panier n'est vidé qu'ici, sur une commande DÉFINITIVEMENT acquise. C'est
  // la modale qui décide de l'instant (réservation confirmée, ou capture PayPal
  // confirmée — jamais avant, sinon un client qui abandonne sur PayPal se
  // retrouverait sans panier ET sans commande) ; la page, elle, possède le
  // panier. Sans ce geste, le client retrouve son panier intact après paiement
  // et peut recommander les mêmes articles — en mode réservation, sa deuxième
  // tentative se heurterait à un 409 sur des produits déjà passés `reserved`.
  //
  // La modale n'est PAS fermée ici. Elle porte la confirmation — référence de
  // réservation, échéance de retrait, montant — et c'est au client de la
  // refermer. Fermer d'office la démonterait dans le même lot de rendu que
  // `clear()` : React 18 ne commit qu'une fois, la confirmation ne serait jamais
  // peinte et le client verrait un panier vide sans savoir ce qu'il a commandé.
  const handleSuccess = () => {
    clear();
    setLineErrors([]);
    setDrift({});
  };

  const allInStock = items.length > 0 && items.every((item) => item.availability === IN_STOCK);

  // Une ligne bloquante (retirée du catalogue, vendue, en rupture) ne passera PAS le
  // serveur : la spec ferme le checkout tant qu'elle est au panier. Sans ce verrou, le
  // client remplit ses coordonnées, accepte les conditions, valide, et récolte un refus
  // qui referme le tunnel — en invité, ses champs sont perdus.
  // Dérivé d'`items` et non des clés de `drift` : un verdict resté derrière une ligne
  // déjà retirée ne doit pas condamner un panier qui ne la contient plus.
  const hasBlockingDrift = items.some((item) => drift[item.product_id]?.blocking);

  return (
    <div data-testid="cart-page">
      <Helmet>
        <title>{`Mon panier — ${BUSINESS.name}`}</title>
        {/* Un panier est propre à un visiteur : rien à faire dans un index. */}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* ─── HERO PANIER (fond sombre + image atelier) ─── */}
      <section className="relative bg-primary text-white py-10 md:py-14 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-45"
          style={{ backgroundImage: `url('${HERO_IMAGES.workshop}')` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/70 to-primary/20" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <span className="font-accent text-xs uppercase tracking-widest text-secondary">Votre sélection</span>
          <h1 className="font-heading font-bold text-3xl md:text-5xl uppercase tracking-tight leading-[1.05] mt-2" data-testid="cart-title">
            Mon panier
          </h1>
          <p className="text-slate-300 mt-4 text-base max-w-xl">
            {count === 0
              ? 'Votre panier est vide pour le moment.'
              : `${count} article${count > 1 ? 's' : ''} — retrait en boutique à Meaux, ${BUSINESS.creneauRetrait} du lundi au vendredi.`}
          </p>
        </div>
      </section>

      {/* ─── CONTENU (fond clair, cartes blanches comme le catalogue) ─── */}
      <section className="bg-slate-50 py-10 md:py-14 min-h-[40vh]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">

          {items.length === 0 ? (
            <div
              className="bg-white border border-slate-200 rounded-sm px-6 py-16 text-center max-w-2xl mx-auto"
              data-testid="cart-empty"
            >
              <PackageOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h2 className="font-heading text-xl uppercase tracking-wider text-primary mb-2">
                Votre panier est vide
              </h2>
              <p className="text-slate-600 text-sm mb-6">
                Parcourez le catalogue : nos PC neufs et reconditionnés sont testés en
                atelier et se retirent en boutique à Meaux.
              </p>
              <Link
                to="/catalogue"
                className="inline-flex items-center gap-2 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider py-3 px-6 rounded-sm text-sm transition-colors"
                data-testid="cart-empty-catalogue"
              >
                Voir le catalogue
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-6 lg:items-start">

              {/* ── Lignes ── */}
              <div className="space-y-3" data-testid="cart-lines">
                {items.map((item) => {
                  const id = item.product_id;
                  const isUnique = (item.condition || UNIQUE_CONDITION) === UNIQUE_CONDITION;
                  const max = maxQtyFor(item);
                  const { Icon: AvailabilityIcon, label: availabilityLabel } = availabilityInfo(item);
                  const image = getImageUrl(item.image);
                  const lineError = errorByProduct.get(id);
                  const lineDrift = drift[id];
                  // Aucune option servie pour cette ligne = ligne non éligible,
                  // ou barème hors service : on ne propose rien du tout.
                  const lineWarrantyOptions = warrantyOptions[id] || [];
                  const chosenWarranty = warrantyByProduct.get(id);
                  // Le refus porte sur la garantie et une garantie est bien
                  // choisie ici : le client peut la retirer seule et poursuivre.
                  const warrantyRefused = Boolean(chosenWarranty) && isWarrantyError(lineError);

                  return (
                    <article
                      key={id}
                      className="bg-white border border-slate-200 rounded-sm overflow-hidden"
                      data-testid={`cart-line-${id}`}
                    >
                      <div className="flex flex-col sm:flex-row gap-4 p-4">

                        {/* Photo — volontairement non cliquable : le nom juste à
                            côté mène déjà à la fiche. Un second lien vers la même
                            cible n'apporte rien, et le masquer aux lecteurs
                            d'écran (aria-hidden sur un élément interactif) serait
                            une violation ARIA. */}
                        <div className="shrink-0 w-full sm:w-28 h-28 bg-slate-100 rounded-sm flex items-center justify-center p-3">
                          {image
                            ? <img src={image} alt="" className="max-w-full max-h-full object-contain" />
                            : <Monitor className="w-10 h-10 text-slate-300" aria-hidden="true" />}
                        </div>

                        {/* Identité + disponibilité */}
                        <div className="flex-1 min-w-0 flex flex-col gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`font-accent text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
                              isUnique
                                ? 'bg-secondary/10 text-secondary border-secondary/30'
                                : 'bg-emerald-50 text-emerald-700 border-emerald-300'
                            }`}>
                              {isUnique ? 'Reconditionné' : 'Neuf'}
                            </span>
                            <span className="inline-flex items-center gap-1.5 font-accent text-[10px] uppercase tracking-wider text-emerald-700">
                              <AvailabilityIcon className="w-3.5 h-3.5" />
                              {availabilityLabel}
                            </span>
                          </div>

                          <Link
                            to={`/produit/${id}`}
                            className="font-heading font-semibold uppercase text-sm leading-tight tracking-tight text-primary hover:text-secondary transition-colors"
                            data-testid={`cart-line-name-${id}`}
                          >
                            {item.name}
                          </Link>

                          <span className="text-xs text-slate-500">
                            Prix unitaire : <strong className="text-slate-700">{formatPrice(item.price)}</strong> TTC
                          </span>

                          {/* Quantité + suppression */}
                          <div className="flex items-center gap-3 mt-auto pt-1">
                            <div
                              className="inline-flex items-center border border-slate-200 rounded-sm bg-white"
                              data-testid={`cart-qty-${id}`}
                            >
                              <button
                                type="button"
                                onClick={() => changeQty(id, item.qty - 1)}
                                disabled={isUnique || item.qty <= 1}
                                aria-label={`Diminuer la quantité de ${item.name}`}
                                data-testid={`cart-qty-minus-${id}`}
                                className="px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 disabled:text-slate-300 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
                              >
                                <Minus className="w-3.5 h-3.5" />
                              </button>
                              <span
                                className="px-3 text-sm font-semibold tabular-nums text-primary"
                                data-testid={`cart-qty-value-${id}`}
                              >
                                {item.qty}
                              </span>
                              <button
                                type="button"
                                onClick={() => changeQty(id, item.qty + 1)}
                                disabled={isUnique || item.qty >= max}
                                aria-label={`Augmenter la quantité de ${item.name}`}
                                data-testid={`cart-qty-plus-${id}`}
                                className="px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 disabled:text-slate-300 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
                              >
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                            </div>

                            {isUnique && (
                              <span className="text-[10px] font-accent uppercase tracking-wider text-slate-500">
                                Pièce unique
                              </span>
                            )}

                            <button
                              type="button"
                              onClick={() => remove(id)}
                              aria-label={`Retirer ${item.name} du panier`}
                              data-testid={`cart-remove-${id}`}
                              className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-slate-500 hover:text-rose-600 px-2 py-1.5 rounded-sm transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">Retirer</span>
                            </button>
                          </div>
                        </div>

                        {/* Sous-total */}
                        <div className="sm:text-right sm:w-32 shrink-0">
                          <span className="block font-accent text-[10px] uppercase tracking-widest text-slate-500">
                            Sous-total
                          </span>
                          <span
                            className="font-heading font-bold text-xl text-secondary leading-none"
                            data-testid={`cart-line-total-${id}`}
                          >
                            {formatPrice((Number(item.price) || 0) * (Number(item.qty) || 0))}
                          </span>
                        </div>
                      </div>

                      {/* ── Garantie Atelier ──
                          Ce n'est pas une extension de garantie : elle ne
                          prolonge rien, elle vend de la MAIN-D'ŒUVRE. Le libellé
                          affiché est celui que sert le serveur, jamais
                          reconstruit ici. Les trois arguments disent ce qui est
                          offert (le temps de l'atelier, le diagnostic) et ce qui
                          ne l'est pas (les pièces) — l'exclusion est dite
                          d'entrée, en avantage, et non découverte au devis. */}
                      {lineWarrantyOptions.length > 0 && (
                        <fieldset
                          className="border-t border-slate-200 px-4 py-3"
                          data-testid={`cart-warranty-${id}`}
                        >
                          <legend className="font-accent text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                            <ShieldCheck className="w-3.5 h-3.5 text-secondary" aria-hidden="true" />
                            Garantie Atelier
                          </legend>

                          <div className="flex flex-wrap gap-2 mt-2">
                            <label
                              className={`cursor-pointer border rounded-sm px-3 py-2 text-xs transition-colors ${
                                chosenWarranty
                                  ? 'border-slate-200 text-slate-600 hover:border-slate-300'
                                  : 'border-secondary bg-secondary/5 text-primary'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`garantie-${id}`}
                                className="sr-only"
                                checked={!chosenWarranty}
                                onChange={() => setWarranty(id, null)}
                                data-testid={`cart-warranty-${id}-none`}
                              />
                              {/* « Sans garantie » tout court se lirait comme une
                                  renonciation : le client garde ses 2 ans de
                                  garantie légale quoi qu'il choisisse. Le refus
                                  porte sur l'OPTION, il faut la nommer. */}
                              <span className="font-semibold uppercase tracking-wider">
                                Sans Garantie Atelier
                              </span>
                            </label>

                            {lineWarrantyOptions.map((option) => {
                              const selected = chosenWarranty?.years === option.years;
                              return (
                                <label
                                  key={option.years}
                                  className={`cursor-pointer border rounded-sm px-3 py-2 text-xs transition-colors ${
                                    selected
                                      ? 'border-secondary bg-secondary/5 text-primary'
                                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name={`garantie-${id}`}
                                    className="sr-only"
                                    checked={selected}
                                    onChange={() => setWarranty(id, option)}
                                    data-testid={`cart-warranty-${id}-${option.years}`}
                                  />
                                  <span className="block font-semibold uppercase tracking-wider">
                                    {option.service_label}
                                  </span>
                                  <span className="block text-[11px] text-slate-500 mt-0.5">
                                    {formatPrice(option.price)} par machine
                                  </span>
                                </label>
                              );
                            })}
                          </div>

                          <ul
                            className="mt-3 space-y-1 text-[11px] leading-snug text-slate-600"
                            data-testid={`cart-warranty-${id}-arguments`}
                          >
                            {/* Le premier argument dit ce que l'option AJOUTE à
                                la garantie légale. Sans lui, le périmètre décrit
                                recouvre une protection gratuite et d'ordre
                                public : le client paierait pour ce qu'il a déjà.
                                Formulation alignée mot pour mot sur l'article 10
                                des CGV. */}
                            <li className="flex items-start gap-1.5">
                              <Check className="w-3 h-3 text-secondary shrink-0 mt-0.5" aria-hidden="true" />
                              Usure, panne tardive, casse, nettoyage, réinstallation :
                              tout ce que la garantie légale ne prend pas en charge
                            </li>
                            <li className="flex items-start gap-1.5">
                              <Check className="w-3 h-3 text-secondary shrink-0 mt-0.5" aria-hidden="true" />
                              Main-d’œuvre en atelier à Meaux, sans limite de nombre d’interventions
                            </li>
                            <li className="flex items-start gap-1.5">
                              <Check className="w-3 h-3 text-secondary shrink-0 mt-0.5" aria-hidden="true" />
                              Diagnostic et devis toujours sans frais
                            </li>
                            <li className="flex items-start gap-1.5">
                              <Check className="w-3 h-3 text-secondary shrink-0 mt-0.5" aria-hidden="true" />
                              Pièces au prix coûtant, jamais posées sans votre accord
                            </li>
                          </ul>

                          {/* Seul écran du parcours où le client paie, et le seul
                              d'où la garantie légale était absente. Elle est due
                              quel que soit son choix : le dire ici évite de faire
                              acheter une protection qu'il a déjà. */}
                          <p
                            className="text-[11px] leading-snug text-slate-500 mt-2"
                            data-testid={`cart-warranty-${id}-legale`}
                          >
                            Vos <strong className="font-semibold">2 ans de garantie légale de conformité</strong>
                            {' '}s’appliquent dans tous les cas, avec ou sans cette option.
                          </p>

                          {chosenWarranty && (
                            <p
                              className="text-xs text-slate-600 mt-2"
                              data-testid={`cart-warranty-total-${id}`}
                            >
                              Garantie Atelier retenue :{' '}
                              <strong className="text-slate-700">
                                {item.qty > 1
                                  ? `${item.qty} × ${formatPrice(chosenWarranty.price)} = ${formatPrice(chosenWarranty.price * item.qty)}`
                                  : formatPrice(chosenWarranty.price)}
                              </strong>{' '}
                              TTC.
                            </p>
                          )}
                        </fieldset>
                      )}

                      {/* Écart constaté à la relecture de la fiche (prix,
                          disponibilité, retrait du catalogue). Ambre : c'est un
                          avertissement, pas un refus. */}
                      {lineDrift && (
                        <div
                          className={`flex flex-wrap items-center gap-3 border-t px-4 py-3 ${
                            lineDrift.blocking
                              ? 'border-amber-300 bg-amber-100'
                              : 'border-amber-200 bg-amber-50'
                          }`}
                          data-testid={`cart-line-changed-${id}`}
                          role="status"
                        >
                          <Info className="w-4 h-4 text-amber-700 shrink-0" />
                          <span className="text-sm text-amber-900 flex-1 min-w-0">
                            {lineDrift.messages.join(' ')}
                          </span>
                          {lineDrift.blocking && (
                            <button
                              type="button"
                              onClick={() => remove(id)}
                              data-testid={`cart-changed-remove-${id}`}
                              className="text-xs font-bold uppercase tracking-wider text-white bg-amber-700 hover:bg-amber-800 px-3 py-1.5 rounded-sm transition-colors"
                            >
                              Retirer du panier
                            </button>
                          )}
                        </div>
                      )}

                      {/* Refus serveur pour CETTE ligne (409 cart_invalid) */}
                      {lineError && (
                        <div
                          className="flex flex-wrap items-center gap-3 border-t border-rose-200 bg-rose-50 px-4 py-3"
                          data-testid={`cart-line-error-${id}`}
                          role="alert"
                        >
                          <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                          <span className="text-sm text-rose-700 flex-1 min-w-0">{lineError.message}</span>
                          {Number.isFinite(Number(lineError.max_qty)) && Number(lineError.max_qty) > 0 && (
                            <button
                              type="button"
                              onClick={() => changeQty(id, Number(lineError.max_qty))}
                              data-testid={`cart-error-adjust-${id}`}
                              className="text-xs font-bold uppercase tracking-wider text-rose-700 border border-rose-300 hover:bg-rose-100 px-3 py-1.5 rounded-sm transition-colors"
                            >
                              Ramener à {Number(lineError.max_qty)}
                            </button>
                          )}
                          {/* Remède propre à un refus de garantie : la machine
                              reste au panier, la commande repart aussitôt. */}
                          {warrantyRefused && (
                            <button
                              type="button"
                              onClick={() => { setWarranty(id, null); forgetError(id); }}
                              data-testid={`cart-error-warranty-remove-${id}`}
                              className="text-xs font-bold uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 px-3 py-1.5 rounded-sm transition-colors"
                            >
                              Retirer la garantie
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => remove(id)}
                            data-testid={`cart-error-remove-${id}`}
                            className={warrantyRefused
                              // Rétrogradé au second plan : retirer le PC reste
                              // possible, mais ce n'est plus le geste attendu.
                              ? 'text-xs font-bold uppercase tracking-wider text-rose-700 border border-rose-300 hover:bg-rose-100 px-3 py-1.5 rounded-sm transition-colors'
                              : 'text-xs font-bold uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 px-3 py-1.5 rounded-sm transition-colors'}
                          >
                            Retirer du panier
                          </button>
                        </div>
                      )}
                    </article>
                  );
                })}

                {orphanErrors.length > 0 && (
                  <div
                    className="border border-rose-200 bg-rose-50 rounded-sm px-4 py-3 space-y-1"
                    data-testid="cart-errors"
                    role="alert"
                  >
                    {orphanErrors.map((error, index) => (
                      <p key={`${error?.product_id || 'panier'}-${index}`} className="text-sm text-rose-700">
                        {error?.message || 'Cette ligne ne peut plus être commandée.'}
                      </p>
                    ))}
                  </div>
                )}

                {/* ── Accessoires suggérés ──
                    Même grammaire que les cartes du catalogue : fond blanc,
                    bord fin, angles droits. Rien ne signale un chargement en
                    cours : tant qu'il n'y a rien à proposer, la section n'existe
                    pas, et son absence ne se remarque pas. */}
                {openSuggestions.length > 0 && (
                  <section
                    className="bg-white border border-slate-200 rounded-sm p-5"
                    data-testid="cart-addons"
                  >
                    <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-primary pb-3 border-b border-slate-200 flex items-center gap-2">
                      <span className="inline-block w-2 h-2 bg-secondary rounded-sm" />
                      Complétez votre commande
                    </h2>
                    <p className="text-xs text-slate-500 mt-3">
                      Des accessoires testés en atelier, à retirer avec votre matériel.
                    </p>

                    <ul className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                      {openSuggestions.map((suggestion) => {
                        const addonId = suggestion.id ?? suggestion.product_id;
                        const addonImage = getImageUrl(suggestion.images?.[0]);
                        return (
                          <li
                            key={addonId}
                            className="border border-slate-200 rounded-sm p-3 flex flex-col gap-2"
                            data-testid={`cart-addon-${addonId}`}
                          >
                            <div className="h-20 bg-slate-100 rounded-sm flex items-center justify-center p-2">
                              {addonImage
                                ? <img src={addonImage} alt="" className="max-w-full max-h-full object-contain" />
                                : <Monitor className="w-8 h-8 text-slate-300" aria-hidden="true" />}
                            </div>
                            <Link
                              to={`/produit/${addonId}`}
                              className="font-heading font-semibold uppercase text-xs leading-tight tracking-tight text-primary hover:text-secondary transition-colors line-clamp-2"
                            >
                              {suggestion.name}
                            </Link>
                            <span className="font-heading font-bold text-base text-secondary leading-none mt-auto">
                              {formatPrice(suggestion.price)}
                            </span>
                            <button
                              type="button"
                              onClick={() => addItem(addableFromApiProduct(suggestion), 1)}
                              aria-label={`Ajouter ${suggestion.name} au panier`}
                              data-testid={`cart-addon-add-${addonId}`}
                              className="inline-flex items-center justify-center gap-1.5 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider py-2 rounded-sm text-[11px] transition-colors"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              Ajouter
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                )}

                <Link
                  to="/catalogue"
                  className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-secondary hover:bg-secondary/10 px-2.5 py-2 rounded-sm transition-colors"
                  data-testid="cart-continue"
                >
                  Continuer mes achats
                </Link>
              </div>

              {/* ── Récapitulatif ── */}
              <aside className="mt-6 lg:mt-0 lg:sticky lg:top-[7.5rem]">
                <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4">
                  <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-primary pb-3 border-b border-slate-200 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-secondary rounded-sm" />
                    Récapitulatif
                  </h2>

                  {/* La Garantie Atelier est détaillée à part : le client doit
                      voir ce qu'il a ajouté au prix du matériel, pas un total qui
                      a grossi sans explication. */}
                  {warrantyTotal > 0 && (
                    <div
                      className="flex items-baseline justify-between gap-3 text-sm"
                      data-testid="cart-warranty-summary"
                    >
                      <span className="text-slate-600">Garantie Atelier</span>
                      <span className="font-semibold text-slate-700">
                        {formatPrice(warrantyTotal)}
                      </span>
                    </div>
                  )}

                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-accent text-xs uppercase tracking-widest text-slate-500">
                      Total TTC
                    </span>
                    <span
                      className="font-heading font-bold text-2xl text-secondary leading-none"
                      data-testid="cart-total"
                    >
                      {formatPrice(total)}
                    </span>
                  </div>

                  {/* Le mode de règlement est décidé par le serveur ; on annonce
                      ici ce qui découle de la composition du panier. */}
                  <div
                    className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-sm ${
                      allInStock
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                        : 'bg-secondary/5 border-secondary/30 text-slate-700'
                    }`}
                    data-testid="cart-mode"
                  >
                    {allInStock
                      ? <PackageCheck className="w-5 h-5 shrink-0 text-emerald-700" />
                      : <CreditCard className="w-5 h-5 shrink-0 text-secondary" />}
                    <span className="leading-relaxed">
                      {allInStock
                        ? 'Réservation gratuite, règlement au comptoir.'
                        : 'Paiement en ligne à la commande.'}
                    </span>
                  </div>

                  {/* Fraîcheur de l'affichage. Tant que la relecture n'a pas
                      abouti, on le dit ; si elle n'a pas pu aboutir, on annonce
                      la réserve plutôt que de laisser croire à un prix ferme. */}
                  {freshness === 'loading' && (
                    <p
                      className="flex items-center gap-2 text-xs text-slate-500"
                      data-testid="cart-freshness-loading"
                    >
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Vérification des prix et des disponibilités…
                    </p>
                  )}
                  {freshness === 'partial' && (
                    <p
                      className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2"
                      data-testid="cart-freshness-warning"
                    >
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                      Certaines fiches n’ont pas pu être vérifiées : montant affiché sous
                      réserve de disponibilité et de tarif à la validation.
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={() => setCheckoutOpen(true)}
                    disabled={hasBlockingDrift}
                    data-testid="cart-checkout"
                    className="w-full bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm py-3.5 text-sm transition-colors disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-200 disabled:hover:bg-slate-100 disabled:hover:text-slate-400 disabled:cursor-not-allowed"
                  >
                    Valider ma commande
                  </button>

                  {hasBlockingDrift && (
                    <p
                      className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2"
                      data-testid="cart-checkout-blocked"
                      role="status"
                    >
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-700 shrink-0 mt-0.5" />
                      Retirez l’article signalé ci-dessus pour valider votre commande.
                    </p>
                  )}

                  <div className="pt-3 border-t border-slate-200 space-y-2 text-xs text-slate-600">
                    <p className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-secondary shrink-0" />
                      Retrait à {BUSINESS.ville} — {BUSINESS.adresse}
                    </p>
                    <p className="flex items-start gap-2">
                      <ShieldCheck className="w-4 h-4 text-secondary shrink-0" />
                      Matériel testé en atelier, garantie incluse.
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          )}
        </div>
      </section>

      {checkoutOpen && (
        <SpecialOrderModal
          cartItems={items}
          cartWarranties={warranties}
          maintenance={maintenance}
          onClose={() => setCheckoutOpen(false)}
          onSuccess={handleSuccess}
          onCartInvalid={handleCartInvalid}
        />
      )}
    </div>
  );
}
